import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { eventsApi, venuesApi } from "../../api";
import { QueryErrorBanner, KpiStripSkeleton } from "../../components/feedback/QueryFeedback";
import { useAuth } from "../../context/AuthContext";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { queryKeys } from "../../queryKeys";
import type { EventListItem, Venue } from "../../types";
import { EventFinanceCard } from "./EventFinanceCard";
import "./FinanceLayout.css";

const FinanceEventsPage: React.FC = () => {
  const { token } = useAuth();
  const workspace = APP_WORKSPACE;
  const [openId, setOpenId] = useState<string | null>(null);

  const eventsQuery = useQuery({
    queryKey: token ? queryKeys.events.list(workspace) : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.list(token!),
    enabled: Boolean(token),
  });

  const venuesQuery = useQuery({
    queryKey: token ? queryKeys.venues.list(workspace) : queryKeys.venues.all(workspace),
    queryFn: () => venuesApi.list(token!),
    enabled: Boolean(token),
  });

  const events = useMemo<EventListItem[]>(() => {
    const data = eventsQuery.data;
    return Array.isArray(data) ? data : [];
  }, [eventsQuery.data]);

  const venuesById = useMemo(() => {
    const map = new Map<string, Venue>();
    const venues: Venue[] = venuesQuery.data ?? [];
    venues.forEach((venue) => map.set(venue.id, venue));
    return map;
  }, [venuesQuery.data]);

  return (
    <div className="finance-events">
      <h2 className="finance-events__title">Venue/Event Analytics</h2>

      {eventsQuery.isError ? (
        <QueryErrorBanner
          message="Unable to load events"
          onRetry={() => void eventsQuery.refetch()}
          retrying={eventsQuery.isFetching}
        />
      ) : null}

      {eventsQuery.isPending ? (
        <KpiStripSkeleton count={4} />
      ) : events.length === 0 ? (
        <div className="eventos-empty">No events yet.</div>
      ) : (
        <div className="finance-events__list">
          {events.map((event) => (
            <EventFinanceCard
              key={event.id}
              event={event}
              workspace={workspace}
              token={token}
              venue={event.venue_id ? venuesById.get(event.venue_id) : undefined}
              expanded={openId === event.id}
              onToggle={() => setOpenId((current) => (current === event.id ? null : event.id))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FinanceEventsPage;
